import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { onboardingAPI } from '../services/api';
import toast from 'react-hot-toast';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { user, activeRole } = useAuthStore();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const checkOnboardingStatus = useCallback(async () => {
    if (!user || !activeRole) {
      setIsChecking(false);
      return;
    }

    // Only check onboarding for users currently acting as clients
    if (activeRole !== 'CLIENT') {
      setIsChecking(false);
      return;
    }

    try {
      const response = await onboardingAPI.getStatus();
      const { isComplete } = response.data;

      if (!isComplete) {
        setNeedsOnboarding(true);
        toast('Please complete your onboarding to access the dashboard', {
          icon: 'ℹ️',
          duration: 4000,
        });
        navigate('/onboarding');
        return;
      }

      setNeedsOnboarding(false);
    } catch (error: any) {
      // If the API call fails, assume they need onboarding
      if (error.response?.status === 404 || error.response?.status === 400) {
        setNeedsOnboarding(true);
        toast('Please complete your onboarding to access the dashboard', {
          icon: 'ℹ️',
          duration: 4000,
        });
        navigate('/onboarding');
        return;
      }
      
      // For other errors, let them proceed but show an error
      console.error('Error checking onboarding status:', error);
      toast.error('Unable to check onboarding status');
    } finally {
      setIsChecking(false);
    }
  }, [user, activeRole, navigate]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your profile...</p>
        </div>
      </div>
    );
  }

  if (needsOnboarding) {
    // This component will unmount when navigate() is called
    return null;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
